import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../lib/i18n';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Loader2,
  ClipboardList,
  ArrowLeft,
  Gamepad2,
  Star,
  CheckCircle,
  FileText,
} from 'lucide-react';

const EMPTY_FORM = {
  name: '',
  description: '',
  version: '1.0',
  fields_schema: '{\n  "type": "object",\n  "required": [],\n  "additionalProperties": false,\n  "properties": {}\n}',
  layout: '{\n  "sections": []\n}',
  is_default: false,
};

function parseJson(str) {
  try { return JSON.parse(str); } catch { return null; }
}

export default function AdminSystemTemplatesPage() {
  const { systemId } = useParams();
  const { language } = useAuth();
  const navigate = useNavigate();

  const [systemInfo, setSystemInfo] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [jsonErrors, setJsonErrors] = useState({ schema: '', layout: '' });
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTarget, setPreviewTarget] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sysRes, tplsRes] = await Promise.all([
        axios.get(`/api/game-systems/${systemId}`),
        axios.get(`/api/game-systems/${systemId}/templates`),
      ]);
      setSystemInfo(sysRes.data);
      setTemplates(tplsRes.data);
    } catch (err) {
      toast.error(t('error_generic', language));
    } finally {
      setLoading(false);
    }
  }, [systemId, language]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditTarget(null);
    setFormData(EMPTY_FORM);
    setJsonErrors({ schema: '', layout: '' });
    setFormOpen(true);
  };

  const openEdit = (tpl) => {
    setEditTarget(tpl);
    setFormData({
      name: tpl.name,
      description: tpl.description || '',
      version: tpl.version || '1.0',
      fields_schema: JSON.stringify(tpl.fields_schema, null, 2),
      layout: JSON.stringify(tpl.layout, null, 2),
      is_default: tpl.is_default,
    });
    setJsonErrors({ schema: '', layout: '' });
    setFormOpen(true);
  };

  const validateJson = () => {
    const errors = { schema: '', layout: '' };
    if (!parseJson(formData.fields_schema)) {
      errors.schema = t('admin_json_invalid', language);
    }
    if (!parseJson(formData.layout)) {
      errors.layout = t('admin_json_invalid', language);
    }
    setJsonErrors(errors);
    return !errors.schema && !errors.layout;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) { toast.error(t('error_required', language)); return; }
    if (!validateJson()) return;

    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        version: formData.version,
        fields_schema: parseJson(formData.fields_schema),
        layout: parseJson(formData.layout),
        is_default: formData.is_default,
      };

      if (editTarget) {
        await axios.patch(`/api/system-templates/${editTarget.id}`, payload);
      } else {
        await axios.post(`/api/game-systems/${systemId}/templates`, payload);
      }
      toast.success(t('success_saved', language));
      setFormOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setSaving(false);
    }
  };

  const handleSetDefault = async (tpl) => {
    try {
      await axios.patch(`/api/system-templates/${tpl.id}`, { is_default: true });
      toast.success(t('success_saved', language));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    try {
      await axios.delete(`/api/system-templates/${deleteDialog.id}`);
      toast.success(t('success_deleted', language));
      setDeleteDialog(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('error_generic', language));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <button
              onClick={() => navigate('/admin/game-systems')}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-2"
            >
              <ArrowLeft className="h-4 w-4" />
              {t('admin_game_systems', language)}
            </button>
            <h1 className="text-2xl font-cinzel font-bold flex items-center gap-2">
              <ClipboardList className="h-6 w-6" />
              {t('admin_system_templates', language)}
              {systemInfo && (
                <Badge variant="secondary" className="text-sm ml-1">
                  <Gamepad2 className="h-3.5 w-3.5 mr-1" />
                  {systemInfo.name}
                </Badge>
              )}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {t('admin_system_templates_desc', language)}
            </p>
          </div>
          <Button onClick={openCreate} className="fantasy-button">
            <Plus className="mr-2 h-4 w-4" />
            {t('admin_template_create', language)}
          </Button>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center h-32">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : templates.length === 0 ? (
          <Card className="glass-card">
            <CardContent className="py-12 text-center">
              <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">
                {t('admin_template_none', language)}
              </p>
              <Button variant="outline" className="mt-4" onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                {t('admin_template_create', language)}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {templates.map(tpl => (
              <Card key={tpl.id} className="glass-card">
                <CardContent className="p-4 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="p-2 rounded-lg bg-primary/10 shrink-0">
                      <ClipboardList className="h-5 w-5 text-primary" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-cinzel font-semibold">{tpl.name}</span>
                        <span className="text-xs text-muted-foreground">v{tpl.version}</span>
                        {tpl.is_default && (
                          <Badge variant="default" className="text-xs gap-1">
                            <Star className="h-3 w-3" />
                            {t('admin_template_default_badge', language)}
                          </Badge>
                        )}
                      </div>
                      {tpl.description && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-sm">
                          {tpl.description}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {tpl.layout?.sections?.length || 0}
                        {t('admin_template_sections_count', language)}
                        {' · '}
                        {Object.keys(tpl.fields_schema?.properties || {}).length}
                        {t('admin_template_fields_count', language)}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/admin/system-templates/${tpl.id}/pdf-builder`)}
                      title={t('admin_template_pdf_builder', language)}
                    >
                      <FileText className="mr-1.5 h-3.5 w-3.5" />
                      {tpl.pdf_asset_id ? 'PDF Builder \u2713' : 'PDF Builder'}
                    </Button>

                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setPreviewTarget(tpl); setPreviewOpen(true); }}
                    >
                      {t('admin_template_preview', language)}
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(tpl)}>
                          <Pencil className="mr-2 h-4 w-4" />
                          {t('edit', language)}
                        </DropdownMenuItem>
                        {!tpl.is_default && (
                          <DropdownMenuItem onClick={() => handleSetDefault(tpl)}>
                            <Star className="mr-2 h-4 w-4" />
                            {t('admin_template_set_default', language)}
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => setDeleteDialog(tpl)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          {t('delete', language)}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle className="font-cinzel">
                {editTarget ? t('edit', language) : t('admin_template_create', language)}
              </DialogTitle>
              <DialogDescription>
                {t('admin_template_form_desc', language)}
              </DialogDescription>
            </DialogHeader>

            <Tabs defaultValue="meta" className="mt-4">
              <TabsList className="w-full">
                <TabsTrigger value="meta" className="flex-1">
                  {t('admin_template_tab_general', language)}
                </TabsTrigger>
                <TabsTrigger value="schema" className="flex-1">
                  {t('admin_template_fields_schema', language)}
                </TabsTrigger>
                <TabsTrigger value="layout" className="flex-1">
                  {t('admin_template_layout', language)}
                </TabsTrigger>
              </TabsList>

              {/* Tab: Général */}
              <TabsContent value="meta" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label htmlFor="tpl-name">{t('admin_template_name', language)} *</Label>
                  <Input
                    id="tpl-name"
                    placeholder="Fiche Personnage Standard"
                    value={formData.name}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="tpl-version">{t('admin_template_version', language)}</Label>
                    <Input
                      id="tpl-version"
                      placeholder="1.0"
                      value={formData.version}
                      onChange={e => setFormData(p => ({ ...p, version: e.target.value }))}
                    />
                  </div>
                  <div className="flex items-end pb-0.5">
                    <div className="flex items-center justify-between w-full">
                      <Label>{t('admin_template_default', language)}</Label>
                      <Switch
                        checked={formData.is_default}
                        onCheckedChange={v => setFormData(p => ({ ...p, is_default: v }))}
                      />
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tpl-desc">{t('admin_template_description', language)}</Label>
                  <Input
                    id="tpl-desc"
                    placeholder={t('admin_placeholder_short_desc', language)}
                    value={formData.description}
                    onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                  />
                </div>
              </TabsContent>

              {/* Tab: JSON Schema */}
              <TabsContent value="schema" className="space-y-2 mt-4">
                <Label>{t('admin_template_fields_schema', language)}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('admin_template_schema_help', language)}
                </p>
                <Textarea
                  value={formData.fields_schema}
                  onChange={e => {
                    setFormData(p => ({ ...p, fields_schema: e.target.value }));
                    setJsonErrors(prev => ({ ...prev, schema: '' }));
                  }}
                  rows={18}
                  className="font-mono text-xs resize-none"
                  spellCheck={false}
                />
                {jsonErrors.schema && (
                  <p className="text-xs text-destructive">{jsonErrors.schema}</p>
                )}
              </TabsContent>

              {/* Tab: Layout */}
              <TabsContent value="layout" className="space-y-2 mt-4">
                <Label>{t('admin_template_layout', language)}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('admin_template_layout_help', language)}
                </p>
                <Textarea
                  value={formData.layout}
                  onChange={e => {
                    setFormData(p => ({ ...p, layout: e.target.value }));
                    setJsonErrors(prev => ({ ...prev, layout: '' }));
                  }}
                  rows={18}
                  className="font-mono text-xs resize-none"
                  spellCheck={false}
                />
                {jsonErrors.layout && (
                  <p className="text-xs text-destructive">{jsonErrors.layout}</p>
                )}
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    {t('admin_template_example', language)}
                  </summary>
                  <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-x-auto">{`{
  "id": "identity",
  "title": "Identité",
  "icon": "user",
  "fields": ["name", "race", "level"],
  "access": {
    "player": "read_write",
    "gm": "read_write",
    "other_players": "read"
  },
  "collapsed_by_default": false
}`}</pre>
                </details>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setFormOpen(false)}>
                {t('cancel', language)}
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t('save', language)}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-cinzel">{previewTarget?.name}</DialogTitle>
            <DialogDescription>v{previewTarget?.version}</DialogDescription>
          </DialogHeader>
          {previewTarget && (
            <div className="space-y-4 mt-2">
              {(previewTarget.layout?.sections || []).map(section => (
                <div key={section.id} className="border rounded-md p-3 space-y-2">
                  <p className="font-semibold text-sm">{section.title}</p>
                  <div className="flex flex-wrap gap-1">
                    {section.fields.map(f => (
                      <code key={f} className="text-xs bg-muted px-1.5 py-0.5 rounded">{f}</code>
                    ))}
                  </div>
                  <div className="flex gap-3 text-xs text-muted-foreground">
                    <span>GM: <strong>{section.access?.gm}</strong></span>
                    <span>Joueur: <strong>{section.access?.player}</strong></span>
                    <span>Autres: <strong>{section.access?.other_players}</strong></span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={open => !open && setDeleteDialog(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-cinzel text-destructive">{t('delete', language)}</DialogTitle>
            <DialogDescription>
              {t('admin_template_confirm_delete', language)}
              {deleteDialog && <span className="block mt-2 font-semibold">{deleteDialog.name}</span>}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>{t('cancel', language)}</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleting}>
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('delete', language)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
